import {
  Mission,
  MissionStatus,
  START_MISSION,
  StartMissionMutation,
  StartMissionMutationVariables,
} from '@edwin-group/cj-graphql';
import Button from '@/components/Button';
import { ArrowPathIcon, ArrowRightIcon, PlayIcon } from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import { useMemo } from 'react';
import { useMutation } from '@apollo/client';
import { useAuthentication } from '@/hooks/useAuthentication';
import { GetCurrentSchoolYearUseCase } from '../../../use-cases/get-current-school-year';
import MissionCard from '@/components/Card/Mission';
import { trackGAEvent } from '@/lib/ga-custom-events/dispatcher';
import { useMissionListingStore } from '../../store/useMissionListingStore';

export const Card = ({ mission }: { mission: Mission }) => {
  const authentication = useAuthentication();
  const school = authentication?.schoolContext;
  const startButtonLabel = useMissionListingStore((state) => state.missionListPage?.resultsSection?.startButton);

  const chaptersSize = mission?.chapters?.length || 0;
  const [startMission, { loading: loadingSubmit }] = useMutation<StartMissionMutation, StartMissionMutationVariables>(
    START_MISSION,
    {
      refetchQueries: 'all',
    },
  );

  const navigate = useNavigate();

  const onPressPlay = () => {
    if (mission.status === MissionStatus.Started) {
      navigate(`/mission/${mission.slug}`);
      return;
    }

    startMission({
      variables: {
        data: {
          schoolYear: GetCurrentSchoolYearUseCase.execute(),
          mission: mission?.documentId,
          school: school?.documentId,
          startedAt: new Date().toISOString(),
        },
      },
    })
      .then(() => {
        trackGAEvent('mission_started', {
          mission_name: mission.roleModel,
          mission_document_id: mission.documentId,
        });
        navigate(`/mission/${mission.slug}`);
      })
      .catch((error) => {
        console.error(error);
      });
  };

  const labelAndIcon = useMemo(() => {
    if (mission.status === MissionStatus.Completed) {
      return { icon: ArrowPathIcon, label: 'Restart' };
    }

    if (mission.status === MissionStatus.Started) {
      return { icon: ArrowRightIcon, label: 'Continue' };
    }

    return { icon: PlayIcon, label: startButtonLabel || 'Start' };
  }, [mission.status, startButtonLabel]);

  return (
    <MissionCard
      mission={mission}
      button={
        <Button onClick={onPressPlay} theme="solid-primary-600" loading={loadingSubmit} size="default" className="mt-4">
          <labelAndIcon.icon className="mr-2 h-6 !text-white" />
          {labelAndIcon.label}
        </Button>
      }
      description={
        <div className="-mb-4">
          <p className="line-clamp-4 font-cadiz text-sh-16-24">
            <span className="font-bold">{chaptersSize} chapters: </span>
            <span>{mission.description}</span>
          </p>
        </div>
      }
      link={`/mission/${mission.slug}`}
    />
  );
};
